import axios from "axios";
import { API_BASE_URL, resolvePublicFileUrl } from "../constants/api-urls";
import superAdminApi from "./superAdminApi";

export const PLATFORM_LOGO_SETTING_KEY = "platform_logo_url";
export const PLATFORM_LOGO_UPDATED_EVENT = "platform-logo-updated";

export const DEFAULT_PLATFORM_LOGO_PATH = "/images/trocanet-logo.png";
export const DEFAULT_PLATFORM_LOGO_DARK_PATH = "/images/trocanet-logo-dark.png";

let cachedLogoPath = DEFAULT_PLATFORM_LOGO_PATH;
let inflightFetch = null;

const extractData = (res) => res?.data?.data ?? res?.data ?? null;

const normalizeMediaPath = (value) => String(value || "").trim().replace(/\\/g, "/");

export function isDefaultPlatformLogoPath(pathOrUrl) {
  const normalized = normalizeMediaPath(pathOrUrl);
  return (
    !normalized ||
    normalized === DEFAULT_PLATFORM_LOGO_PATH ||
    normalized === DEFAULT_PLATFORM_LOGO_DARK_PATH ||
    normalized.endsWith("/images/trocanet-logo.png") ||
    normalized.endsWith("/images/trocanet-logo-dark.png")
  );
}

export function isCustomPlatformLogoPath(pathOrUrl) {
  return !isDefaultPlatformLogoPath(pathOrUrl);
}

function buildPublicAssetUrl(assetPath) {
  const publicPath = `${process.env.PUBLIC_URL || ""}${assetPath}`;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${publicPath.startsWith("/") ? publicPath : `/${publicPath}`}`;
  }
  return publicPath || assetPath;
}

/** Frontend-public default logo (not served from API media). */
export function getDefaultPlatformLogoUrl() {
  return buildPublicAssetUrl(DEFAULT_PLATFORM_LOGO_PATH);
}

/** White wordmark for dark headers (hub home, etc.). */
export function getDefaultPlatformLogoDarkUrl() {
  return buildPublicAssetUrl(DEFAULT_PLATFORM_LOGO_DARK_PATH);
}

export function resolvePlatformLogoUrl(pathOrUrl, { variant = "default" } = {}) {
  const normalized = normalizeMediaPath(pathOrUrl);
  if (isDefaultPlatformLogoPath(normalized)) {
    return variant === "onDark"
      ? getDefaultPlatformLogoDarkUrl()
      : getDefaultPlatformLogoUrl();
  }
  // Static frontend assets under /images stay on the app origin.
  if (normalized.startsWith("/images/")) {
    return buildPublicAssetUrl(normalized);
  }
  return resolvePublicFileUrl(normalized);
}

export function notifyPlatformLogoUpdated(pathOrUrl) {
  const normalized = normalizeMediaPath(pathOrUrl) || DEFAULT_PLATFORM_LOGO_PATH;
  cachedLogoPath = normalized;
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(PLATFORM_LOGO_UPDATED_EVENT, {
        detail: {
          path: normalized,
          url: resolvePlatformLogoUrl(normalized),
        },
      })
    );
  }
}

export async function fetchPublicPlatformLogoPath({ bypassCache = false } = {}) {
  if (!bypassCache && cachedLogoPath) {
    return cachedLogoPath;
  }
  if (!bypassCache && inflightFetch) {
    return inflightFetch;
  }

  inflightFetch = (async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/public/platform/bootstrap`, {
        timeout: 15000,
      });
      const settings = extractData(res)?.settings ?? {};
      const configured = settings[PLATFORM_LOGO_SETTING_KEY];
      if (typeof configured === "string" && configured.trim()) {
        cachedLogoPath = normalizeMediaPath(configured);
        return cachedLogoPath;
      }
    } catch {
      // Fall back to default logo.
    }
    cachedLogoPath = DEFAULT_PLATFORM_LOGO_PATH;
    return cachedLogoPath;
  })();

  try {
    return await inflightFetch;
  } finally {
    inflightFetch = null;
  }
}

export async function fetchPublicPlatformLogoUrl(options = {}) {
  const path = await fetchPublicPlatformLogoPath(options);
  return resolvePlatformLogoUrl(path, options);
}

export async function fetchPlatformLogoSetting() {
  const res = await superAdminApi.get("/super-admin/platform-logo");
  return extractData(res)?.setting ?? null;
}

export async function savePlatformLogoUrl(imagePath) {
  const normalized = String(imagePath || "").trim() || DEFAULT_PLATFORM_LOGO_PATH;
  const res = await superAdminApi.put("/super-admin/platform-logo", {
    setting_value: normalized,
  });
  const setting = extractData(res)?.setting ?? null;
  const savedPath = String(setting?.settingValue || normalized).trim();
  notifyPlatformLogoUpdated(savedPath);
  return setting;
}

export async function uploadPlatformLogoImage(file) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("category", "auth");
  formData.append("companyName", "Platform_Logo");
  formData.append("fileType", "platform_logo");

  const res = await superAdminApi.post("/upload/single", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  const imagePath = res?.data?.data?.accessPath || res?.data?.data?.filePath || null;
  if (!imagePath) {
    throw new Error("Platform logo upload failed.");
  }
  return imagePath;
}
